import React, { useState } from 'react';
import emailService, { AICampaignDesignRequest, EmailCampaign } from '../../../services/emailService';
import { toast } from 'react-hot-toast';

interface AICampaignDesignerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDesigned: (draft: Partial<EmailCampaign>) => void;
}

const AICampaignDesignerModal: React.FC<AICampaignDesignerModalProps> = ({ isOpen, onClose, onDesigned }) => {
  const [form, setForm] = useState<AICampaignDesignRequest>({
    goal: 'pipeline',
    targetAudience: '',
    productDescription: '',
    includeAIContent: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleChange = (field: keyof AICampaignDesignRequest, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.targetAudience || !form.productDescription) {
      toast.error('Target audience and product description are required');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await emailService.designAICampaign(form);
      onDesigned(result.campaignDraft);
    } catch (error) {
      console.error('AI campaign design error:', error);
      toast.error('Failed to design campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-xl">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">AI Campaign Designer</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Close
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Goal</label>
              <select
                value={form.goal}
                onChange={e => handleChange('goal', e.target.value as AICampaignDesignRequest['goal'])}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="pipeline">Pipeline / Lead Gen</option>
                <option value="demo">Book Demos</option>
                <option value="trial">Free Trial</option>
                <option value="nurture">Nurture</option>
                <option value="reactivation">Reactivation</option>
                <option value="event">Event / Webinar</option>
                <option value="referral">Referrals</option>
                <option value="partnership">Partnerships</option>
                <option value="recruiting">Recruiting</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry (optional)</label>
              <input
                type="text"
                value={form.industry || ''}
                onChange={e => handleChange('industry', e.target.value)}
                placeholder="saas, agency, real_estate, ecommerce..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
            <input
              type="text"
              value={form.targetAudience}
              onChange={e => handleChange('targetAudience', e.target.value)}
              placeholder="e.g. Heads of Sales at B2B SaaS companies"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product / Offer Description</label>
            <textarea
              rows={4}
              value={form.productDescription}
              onChange={e => handleChange('productDescription', e.target.value)}
              placeholder="Describe the product, value proposition, and key outcomes you deliver. This will guide the AI."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
              <select
                value={form.tone || ''}
                onChange={e => handleChange('tone', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">Default</option>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="friendly">Friendly</option>
                <option value="direct">Direct</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Length</label>
              <select
                value={form.length || ''}
                onChange={e => handleChange('length', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">Default</option>
                <option value="short">Short</option>
                <option value="medium">Medium</option>
                <option value="long">Long</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Steps (optional)</label>
              <input
                type="number"
                min={1}
                max={10}
                value={form.sequenceLength || ''}
                onChange={e => handleChange('sequenceLength', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="include_ai_content"
              type="checkbox"
              checked={form.includeAIContent ?? true}
              onChange={e => handleChange('includeAIContent', e.target.checked)}
              className="h-4 w-4 text-purple-600 border-gray-300 rounded"
            />
            <label htmlFor="include_ai_content" className="text-sm text-gray-700">
              Generate AI-written email content for each step
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Designing…' : 'Design Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AICampaignDesignerModal;
